import { useState, useCallback } from "react";
import AppHeader from "@/components/AppHeader";
import { elements } from "@/data/elements";
import { balanceEquation } from "@/data/equationBalancer";
import { calculateMolarMass } from "@/data/molarMass";
import {
  type Difficulty,
  equationPools,
  molarMassPools,
  sigFigPools,
  moleConversionPools,
  oxNumberPools,
  ionicNamePools,
  lewisPools,
  stoichiometryPools,
} from "@/data/worksheetPools";
import Main from "@/components/Main";
import { downloadAnswerKeyPdf, reasoningFor } from "@/lib/answerKeyPdf";
import { downloadWorksheetPdf } from "@/lib/worksheetPdf";
import { Settings2, FileText, Calendar, RefreshCw, Sun, Moon, FileDown } from "@/lib/icons";
import Seo from "@/components/Seo";

type TopicKey =
  | "element-id"
  | "electron-config"
  | "equation-balance"
  | "molar-mass"
  | "properties"
  | "sig-figs"
  | "mole-conversion"
  | "ox-number"
  | "ionic-naming"
  | "lewis"
  | "stoichiometry";

interface Topic {
  key: TopicKey;
  label: string;
  description: string;
}

const topics: Topic[] = [
  { key: "element-id", label: "Element Identification", description: "Symbol ↔ Name ↔ Atomic #" },
  { key: "electron-config", label: "Electron Configuration", description: "Write e⁻ config for elements" },
  { key: "equation-balance", label: "Equation Balancing", description: "Balance chemical equations" },
  { key: "molar-mass", label: "Molar Mass", description: "Calculate molar mass of compounds" },
  { key: "properties", label: "Element Properties", description: "Phase, block, electronegativity" },
  { key: "sig-figs", label: "Significant Figures", description: "Count sig figs in numbers" },
  { key: "mole-conversion", label: "Mole Conversions", description: "mol ↔ g, mol ↔ L at STP" },
  { key: "ox-number", label: "Oxidation Numbers", description: "Assign ox# in compounds" },
  { key: "ionic-naming", label: "Naming Ionic Compounds", description: "Formula ↔ name" },
  { key: "lewis", label: "Lewis Structures", description: "Bonding & lone pairs" },
  { key: "stoichiometry", label: "Stoichiometry", description: "Mass-mass & mass-mole problems" },
];

type ViewMode = "practice" | "answer-key";

interface Question {
  id: number;
  topic: TopicKey;
  question: string;
  answer: string;
}

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/**
 * Pick the next item from `pool` whose key (built by `keyFn`) hasn't been used
 * yet for `topic`. Falls back to allowing repeats only if the pool is exhausted.
 */
function pickUnique<T>(
  pool: T[],
  used: Set<string>,
  topic: TopicKey,
  keyFn: (item: T) => string,
): T | null {
  if (!pool.length) return null;
  for (const item of pool) {
    const k = `${topic}::${keyFn(item)}`;
    if (!used.has(k)) {
      used.add(k);
      return item;
    }
  }
  // Pool exhausted - allow a repeat (rare; only when count > pool size)
  return pool[Math.floor(Math.random() * pool.length)];
}

function generateQuestions(
  selectedTopics: TopicKey[],
  count: number,
  difficulty: Difficulty,
): Question[] {
  const questions: Question[] = [];
  let id = 1;
  const perTopic = Math.max(1, Math.ceil(count / selectedTopics.length));

  // Element pool scales with difficulty (legacy behaviour kept)
  const maxZ = difficulty === "easy" ? 20 : difficulty === "medium" ? 56 : 118;
  const elementPool = elements.filter((e) => e.number <= maxZ);

  // Tracks (topic, key) tuples we've already emitted to avoid duplicates
  const used = new Set<string>();

  for (const topic of selectedTopics) {
    const shuffledElements = shuffle(elementPool);
    let elementCursor = 0;

    for (let i = 0; i < perTopic && questions.length < count; i++) {
      switch (topic) {
        case "element-id": {
          // Each element id question has 3 variants - track per (element, variant)
          const el = shuffledElements[elementCursor++ % shuffledElements.length];
          const variant = Math.floor(Math.random() * 3);
          const tupleKey = `${el.number}-${variant}`;
          if (used.has(`${topic}::${tupleKey}`)) {
            // try next variant deterministically
            let found = false;
            for (let v = 0; v < 3 && !found; v++) {
              const k = `${el.number}-${v}`;
              if (!used.has(`${topic}::${k}`)) {
                used.add(`${topic}::${k}`);
                pushElementId(questions, id++, topic, el, v);
                found = true;
              }
            }
            if (!found) {
              used.add(`${topic}::${tupleKey}`);
              pushElementId(questions, id++, topic, el, variant);
            }
          } else {
            used.add(`${topic}::${tupleKey}`);
            pushElementId(questions, id++, topic, el, variant);
          }
          break;
        }

        case "electron-config": {
          const el = pickUnique(shuffledElements, used, topic, (e) => String(e.number));
          if (!el) break;
          questions.push({
            id: id++,
            topic,
            question: `Write the electron configuration for ${el.name} (Z=${el.number}).`,
            answer: el.electronConfig,
          });
          break;
        }

        case "equation-balance": {
          const eq = pickUnique(
            shuffle(equationPools[difficulty]),
            used,
            topic,
            (e) => e,
          );
          if (!eq) break;
          const result = balanceEquation(eq);
          questions.push({
            id: id++,
            topic,
            question: `Balance: ${eq}`,
            answer: result.balanced || "See solution",
          });
          break;
        }

        case "molar-mass": {
          const item = pickUnique(
            shuffle(molarMassPools[difficulty]),
            used,
            topic,
            (m) => m.formula,
          );
          if (!item) break;
          const result = calculateMolarMass(item.formula);
          questions.push({
            id: id++,
            topic,
            question: `Calculate the molar mass of ${item.display}.`,
            answer: `${result.total.toFixed(2)} g/mol`,
          });
          break;
        }

        case "properties": {
          const el = shuffledElements[elementCursor++ % shuffledElements.length];
          const propVariants = [
            { q: `What is the phase of ${el.name} at STP?`, a: el.phase, key: "phase" },
            { q: `What block does ${el.name} belong to?`, a: `${el.block}-block`, key: "block" },
            {
              q: `What is the electronegativity of ${el.name}?`,
              a: el.electronegativity?.toFixed(2) ?? "N/A",
              key: "en",
            },
            { q: `What period is ${el.name} in?`, a: `Period ${el.period}`, key: "period" },
          ];
          // Pick a variant that hasn't been used yet for this element
          const fresh = propVariants.find(
            (p) => !used.has(`${topic}::${el.number}-${p.key}`),
          );
          const pick = fresh ?? propVariants[Math.floor(Math.random() * propVariants.length)];
          used.add(`${topic}::${el.number}-${pick.key}`);
          questions.push({ id: id++, topic, question: pick.q, answer: pick.a });
          break;
        }

        case "sig-figs": {
          const item = pickUnique(
            shuffle(sigFigPools[difficulty]),
            used,
            topic,
            (s) => s.number,
          );
          if (!item) break;
          questions.push({
            id: id++,
            topic,
            question: `How many significant figures are in ${item.number}?`,
            answer: `${item.count} sig figs (${item.reason})`,
          });
          break;
        }

        case "mole-conversion": {
          const item = pickUnique(
            shuffle(moleConversionPools[difficulty]),
            used,
            topic,
            (m) => m.question,
          );
          if (!item) break;
          questions.push({
            id: id++,
            topic,
            question: item.question,
            answer: item.answer,
          });
          break;
        }

        case "ox-number": {
          const item = pickUnique(
            shuffle(oxNumberPools[difficulty]),
            used,
            topic,
            (o) => `${o.formula}-${o.element}`,
          );
          if (!item) break;
          questions.push({
            id: id++,
            topic,
            question: `What is the oxidation number of ${item.element} in ${item.formula}?`,
            answer: item.oxNumber,
          });
          break;
        }

        case "ionic-naming": {
          const item = pickUnique(
            shuffle(ionicNamePools[difficulty]),
            used,
            topic,
            (n) => n.formula,
          );
          if (!item) break;
          // Alternate between formula → name and name → formula
          const askForName = Math.random() < 0.5;
          questions.push({
            id: id++,
            topic,
            question: askForName
              ? `Name the ionic compound: ${item.formula}`
              : `Write the formula for ${item.name}.`,
            answer: askForName ? item.name : item.formula,
          });
          break;
        }

        case "lewis": {
          const item = pickUnique(
            shuffle(lewisPools[difficulty]),
            used,
            topic,
            (l) => l.molecule,
          );
          if (!item) break;
          questions.push({
            id: id++,
            topic,
            question: item.question,
            answer: item.answer,
          });
          break;
        }

        case "stoichiometry": {
          const item = pickUnique(
            shuffle(stoichiometryPools[difficulty]),
            used,
            topic,
            (s) => `${s.equation}::${s.question}`,
          );
          if (!item) break;
          questions.push({
            id: id++,
            topic,
            question: `Given the balanced equation: ${item.equation}\n${item.question}`,
            answer: item.answer,
          });
          break;
        }
      }
    }
  }

  return shuffle(questions).slice(0, count);
}

function pushElementId(
  questions: Question[],
  id: number,
  topic: TopicKey,
  el: (typeof elements)[number],
  variant: number,
) {
  if (variant === 0) {
    questions.push({
      id,
      topic,
      question: `What is the name of the element with symbol "${el.symbol}"?`,
      answer: el.name,
    });
  } else if (variant === 1) {
    questions.push({
      id,
      topic,
      question: `What is the symbol for ${el.name}?`,
      answer: el.symbol,
    });
  } else {
    questions.push({
      id,
      topic,
      question: `What element has atomic number ${el.number}?`,
      answer: `${el.name} (${el.symbol})`,
    });
  }
}

const questionCounts = [5, 10, 20, 30, 50];

export default function WorksheetGenerator() {
  const [selectedTopics, setSelectedTopics] = useState<TopicKey[]>(["element-id", "electron-config"]);
  const [questionCount, setQuestionCount] = useState(10);
  const [difficulty, setDifficulty] = useState<Difficulty>("medium");
  const [questions, setQuestions] = useState<Question[] | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>("practice");
  const [paperPreview, setPaperPreview] = useState(false);

  const toggleTopic = (key: TopicKey) => {
    setSelectedTopics((prev) =>
      prev.includes(key) ? (prev.length > 1 ? prev.filter((t) => t !== key) : prev) : [...prev, key]
    );
  };

  const generate = useCallback(() => {
    if (selectedTopics.length === 0) return;
    setQuestions(generateQuestions(selectedTopics, questionCount, difficulty));
  }, [selectedTopics, questionCount, difficulty]);

  const today = new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

  return (
    <div className="min-h-screen bg-transparent">
      <Seo title="Chemistry Worksheet Generator — ElementPad" description="Generate printable chemistry worksheets with answer keys: nomenclature, balancing, stoichiometry, molar mass, and more." path="/worksheet" />
      <AppHeader />
      <div className="flex max-w-[1200px] mx-auto print:max-w-none">
        {/* Left sidebar - config */}
        <aside className="w-[340px] shrink-0 border-r border-border p-6 space-y-6 print:hidden sticky top-14 h-[calc(100vh-3.5rem)] overflow-y-auto" role="complementary" aria-label="Worksheet settings">
          <div>
            <h1 className="text-xl font-bold text-foreground">Worksheet Generator</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Create practice worksheets for your class
            </p>
          </div>

          {/* Question count */}
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium mb-2">Number of Questions</p>
            <div className="flex gap-2">
              {questionCounts.map((n) => (
                <button
                  key={n}
                  onClick={() => setQuestionCount(n)}
                  className={`w-10 h-10 rounded-full text-sm font-medium transition-colors ${
                    questionCount === n
                      ? "bg-foreground text-background"
                      : "bg-muted text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>

          {/* Topics */}
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium mb-2">Topics</p>
            <div className="space-y-2">
              {topics.map((t) => {
                const selected = selectedTopics.includes(t.key);
                return (
                  <button
                    key={t.key}
                    onClick={() => toggleTopic(t.key)}
                    className={`w-full text-left p-3 rounded-lg border transition-colors ${
                      selected
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-primary/30"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`mt-0.5 w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${
                        selected ? "bg-primary border-primary" : "border-muted-foreground/40"
                      }`}>
                        {selected && (
                          <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                            <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">{t.label}</p>
                        <p className="text-xs text-muted-foreground font-mono">{t.description}</p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Difficulty */}
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium mb-2">Difficulty</p>
            <div className="flex gap-2">
              {(["easy", "medium", "hard"] as Difficulty[]).map((d) => (
                <button
                  key={d}
                  onClick={() => setDifficulty(d)}
                  className={`px-4 py-2 rounded-full text-sm font-medium capitalize transition-colors ${
                    difficulty === d
                      ? "bg-foreground text-background"
                      : "bg-muted text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>

          {/* Live count summary */}
          <p
            className="text-xs text-muted-foreground text-center"
            aria-live="polite"
          >
            <span className="font-semibold text-foreground">{questionCount}</span>{" "}
            question{questionCount === 1 ? "" : "s"} across{" "}
            <span className="font-semibold text-foreground">{selectedTopics.length}</span>{" "}
            topic{selectedTopics.length === 1 ? "" : "s"}
          </p>

          {/* Generate button */}
          <button
            onClick={generate}
            className="w-full flex items-center justify-center gap-2 h-12 rounded-lg bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-colors"
          >
            <Settings2 size={18} />
            {questions ? "Regenerate Worksheet" : "Generate Worksheet"}
          </button>
          <p className="text-xs text-muted-foreground text-center">
            Questions are generated from real, commonly taught chemistry content.
          </p>
        </aside>

        {/* Right - worksheet view */}
        <Main className="flex-1 min-h-[calc(100vh-3.5rem)]">
          {/* Toolbar */}
          {questions && (
            <div className="sticky top-14 z-30 bg-background/80 backdrop-blur-sm border-b border-border px-6 h-12 flex items-center justify-between print:hidden">
              <div className="flex gap-1 bg-muted rounded-lg p-1">
                {(["practice", "answer-key"] as ViewMode[]).map((m) => (
                  <button
                    key={m}
                    onClick={() => setViewMode(m)}
                    className={`text-sm font-medium px-3 py-1 rounded-md capitalize transition-colors ${
                      viewMode === m
                        ? "bg-background text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {m === "answer-key" ? "Answer Key" : m.charAt(0).toUpperCase() + m.slice(1)}
                  </button>
                ))}
              </div>
              <div className="flex gap-1 items-center">
                <button
                  onClick={generate}
                  className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                  aria-label="Regenerate with new random questions"
                  title="Regenerate worksheet"
                >
                  <RefreshCw size={14} />
                  Regenerate
                </button>
                <button
                  onClick={() => setPaperPreview((v) => !v)}
                  className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                  aria-pressed={paperPreview}
                  aria-label={paperPreview ? "Switch to dark preview" : "Switch to paper preview"}
                  title={paperPreview ? "Dark preview" : "Paper preview"}
                >
                  {paperPreview ? <Moon size={14} /> : <Sun size={14} />}
                  {paperPreview ? "Dark" : "Paper"}
                </button>
                <button
                  onClick={() =>
                    downloadWorksheetPdf(
                      questions.map((q, i) => ({
                        index: i + 1,
                        topic: q.topic,
                        question: q.question,
                      })),
                      {
                        difficulty:
                          difficulty.charAt(0).toUpperCase() +
                          difficulty.slice(1),
                        topics: selectedTopics
                          .map((t) => topics.find((x) => x.key === t)?.label)
                          .filter(Boolean)
                          .join(", "),
                        date: today,
                      },
                    )
                  }
                  className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                  title="Download printable worksheet (PDF)"
                >
                  <FileDown size={14} />
                  Worksheet PDF
                </button>
                <button
                  onClick={() =>
                    downloadAnswerKeyPdf(
                      questions.map((q, i) => ({
                        index: i + 1,
                        topic: q.topic,
                        question: q.question,
                        answer: q.answer,
                        reasoning: reasoningFor(q.topic, q.question, q.answer),
                      })),
                      {
                        difficulty:
                          difficulty.charAt(0).toUpperCase() +
                          difficulty.slice(1),
                        topics: selectedTopics
                          .map((t) => topics.find((x) => x.key === t)?.label)
                          .filter(Boolean)
                          .join(", "),
                        date: today,
                      },
                    )
                  }
                  className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                  title="Download answer key with reasoning (PDF)"
                >
                  <FileDown size={14} />
                  Answer key PDF
                </button>
                <span className="flex items-center gap-1.5 text-sm px-3 py-1.5 text-muted-foreground">
                  <Calendar size={14} />
                  {today}
                </span>
              </div>
            </div>
          )}

          {/* Content */}
          <div
            className={`p-6 print:p-0 ${
              paperPreview ? "bg-neutral-200 dark:bg-neutral-800 min-h-[calc(100vh-6.5rem)]" : ""
            }`}
          >
            {!questions ? (
              <div className="flex flex-col items-center justify-center h-[60vh] text-center">
                <FileText size={48} className="text-muted-foreground/30 mb-4" />
                <p className="text-muted-foreground">
                  Click 'Generate Worksheet' to create practice problems
                </p>
              </div>
            ) : (
              <div
                className={`max-w-2xl mx-auto print:max-w-none ${
                  paperPreview
                    ? "bg-white text-black p-10 rounded shadow-xl print:shadow-none print:p-0 print:bg-transparent"
                    : ""
                }`}
              >
                {/* Print header */}
                <div className="mb-6 pb-4 border-b border-border print:border-gray-300">
                  <h2 className="text-lg font-bold text-foreground print:text-black">
                    ElementPad - Chemistry Worksheet
                  </h2>
                  <div className="flex gap-8 mt-2 text-sm text-muted-foreground print:text-gray-500">
                    <span>Name: _______________________</span>
                    <span>Date: _______________</span>
                    <span>Score: _____ / {questions.length}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 print:text-gray-400">
                    Difficulty: {difficulty.charAt(0).toUpperCase() + difficulty.slice(1)} · {questions.length} questions ·{" "}
                    {selectedTopics.map((t) => topics.find((x) => x.key === t)?.label).join(", ")}
                  </p>
                </div>

                {/* Questions */}
                <ol
                  className={
                    viewMode === "answer-key"
                      ? "divide-y divide-border print:divide-gray-300"
                      : "space-y-5"
                  }
                >
                  {questions.map((q, i) => (
                    <li
                      key={q.id}
                      className={`flex gap-3 ${
                        viewMode === "answer-key" ? "py-4 first:pt-0 last:pb-0" : ""
                      }`}
                    >
                      <span className="font-mono text-sm text-muted-foreground min-w-[2rem] pt-0.5 print:text-gray-500">
                        {i + 1}.
                      </span>
                      <div className="flex-1 space-y-2">
                        <p className="text-sm text-foreground print:text-black whitespace-pre-line leading-relaxed">
                          {q.question}
                        </p>

                        {/* Practice mode: input */}
                        {viewMode === "practice" && (
                          <input
                            type="text"
                            placeholder="Your answer..."
                            className="mt-2 w-full h-9 rounded-lg border border-input bg-tile-bg px-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring print:hidden"
                          />
                        )}

                        {/* Answer key */}
                        {viewMode === "answer-key" && (
                          <div className="mt-2 rounded-md border border-primary/20 bg-primary/5 px-3 py-2 print:border-blue-200 print:bg-blue-50">
                            <p className="text-[10px] font-medium uppercase tracking-wider text-primary/70 print:text-blue-700">
                              Answer
                            </p>
                            <p className="mt-0.5 text-sm font-mono text-primary print:text-blue-700 whitespace-pre-line">
                              {q.answer}
                            </p>
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        </Main>
      </div>
    </div>
  );
}
